import {
  Avatar,
  Button,
  Form,
  Input,
  List,
  Radio,
  Tooltip,
} from 'antd';
import { CheckOutlined, GlobalOutlined, LockOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import {
  related_comments_ticket_path,
  ticket_comments_path,
} from 'js-routes/generated/routes';
import useErrors from 'hooks/forms/useErrors';
import I18n from 'i18n-js';
import classNames from 'classnames';
import {
  string, shape, number, bool,
} from 'prop-types';
import CommentList from './CommentList';
import CommentEditor from './CommentEditor';
import useSubmitForm from './hooks/useSubmitForm';
import useUpdateComment from './hooks/useUpdateComment';

const TicketComments = ({
  ticketId = null,
  currentUserInfo = {
    id: null,
    type: '',
    username: '',
    avatarUrl: '',
  },
  canSetInternalComment = false,
}) => {
  const [form] = Form.useForm();
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true);

    const loadComments = async () => {
      const response = await fetch(related_comments_ticket_path(ticketId));
      const data = await response.json();
      setComments(data.comments);
      setIsLoading(false);
    };

    loadComments();
  }, [ticketId]);

  const {
    handleError,
    clearErrors,
  } = useErrors();

  const { handleUpdateComment } = useUpdateComment({ setComments });

  const {
    isFormSending,
    handleSubmitForm,
    submitButtonName,
  } = useSubmitForm({
    form,
    formUrl: ticket_comments_path(),
    clearErrors,
    handleError,
    setComments,
  });

  const isInternal = Form.useWatch('internal', form);
  const content = Form.useWatch('content', form);
  const editorClassName = classNames('ticket-comments-form__editor', {
    'ticket-comments-form__editor--internal': isInternal,
  });

  return (
    <>
      {(isLoading || comments.length > 0) && (
        <CommentList
          ticketId={ticketId}
          comments={comments}
          loading={isLoading}
          onUpdate={handleUpdateComment}
          canSetInternalComment={canSetInternalComment}
        />
      )}

      <List.Item>
        <div className="ticket-comments-form">
          <Avatar
            src={currentUserInfo.avatarUrl}
            size={40}
          />

          <div className={editorClassName}>
            <Form
              component={false}
              form={form}
              layout="vertical"
              onFinish={handleSubmitForm}
              /* eslint-disable camelcase */
              initialValues={{
                author_type: currentUserInfo.type,
                author_id: currentUserInfo.id,
                ticket_id: ticketId,
                internal: false,
              }}
              /* eslint-enable camelcase */
              disabled={isFormSending}
            >
              <Form.Item name="ticket_id" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item name="author_type" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item name="author_id" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                name="content"
                className="ticket-comments-form__content-field"
                getValueFromEvent={(value) => value}
              >
                <CommentEditor
                  ticketId={ticketId}
                  placeholder="Add a comment..."
                />
              </Form.Item>

              <div className="ticket-comments-form__footer">
                {canSetInternalComment ? (
                  <Form.Item
                    name="internal"
                    className="ticket-comments-form__visibility"
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      size="small"
                      className="ticket-comments-form__visibility-toggle"
                      options={[
                        {
                          value: false,
                          label: (
                            <span className="ticket-comments-form__visibility-option">
                              <GlobalOutlined />
                              {I18n.t('ticket_comments.visibility.public')}
                            </span>
                          ),
                        },
                        {
                          value: true,
                          label: (
                            <span className="ticket-comments-form__visibility-option">
                              <LockOutlined />
                              {I18n.t('ticket_comments.visibility.internal')}
                            </span>
                          ),
                        },
                      ]}
                    />
                  </Form.Item>
                ) : <div />}
                <Tooltip title={submitButtonName}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="small"
                    icon={<CheckOutlined />}
                    loading={isFormSending}
                    disabled={!content?.trim()}
                    onClick={() => form.submit()}
                    aria-label={submitButtonName}
                  />
                </Tooltip>
              </div>
            </Form>
          </div>
        </div>
      </List.Item>

    </>
  );
};

TicketComments.propTypes = {
  ticketId: number,
  currentUserInfo: shape({
    id: number,
    type: string,
    username: string,
    avatarUrl: string,
  }),
  canSetInternalComment: bool,
};

export default TicketComments;
